import { type FC, type ReactNode } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Stack,
  Paper,
  Grid,
  Chip,
  Link as MuiLink,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DialogHeader } from "@/shared/components";
import { PagePaths } from "@/partner/shared/constants/routes";
import { PAYMENT_PROVIDER_LABEL_KEY } from "@/partner/shared/utils/paymentProvider";
import type { PurchasesListQueryType } from "@/types/graphql/partner.generated";
import { interpolationFormat } from "@/partner/shared/lib";
import { formatMoney } from "@/partner/shared/utils/formatLocale";
import { findLastSucceededRefund } from "../model/paymentBreakdown";
import { PaymentBreakdown } from "./PaymentBreakdown";
import { PaymentRefundsTable } from "./PaymentRefundsTable";
import {
  RENEW_ERROR_REASON_LABEL_KEYS,
  formatPastDue,
} from "@/partner/features/subscriptions/model/subscriptionLabels";

type PurchaseRow = PurchasesListQueryType["productPurchases"]["nodes"][number];

interface PurchaseDetailsDialogProps {
  open: boolean;
  purchase: PurchaseRow | null;
  onClose: () => void;
}

// Простой лейбл-значение блок, как в UserDialog — read-only деталка без
// формы, поэтому переиспользуем тот же визуальный приём.
const Field: FC<{ label: string; children: ReactNode }> = ({
  label,
  children,
}) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body1" component="div">
      {children}
    </Typography>
  </Box>
);

export const PurchaseDetailsDialog: FC<PurchaseDetailsDialogProps> = ({
  open,
  purchase,
  onClose,
}) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  // Референс ProductPurchaseDialog.js:20,35 — useIsMobile() это
  // breakpoints.down("sm"), на телефоне диалог во весь экран.
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const title = purchase?.product?.title ?? t("purchases.details.title");
  const emptyValue = t("common.emptyDash");
  const payment = purchase?.payment;
  const subscription = purchase?.subscription;
  const refunds = payment?.refunds ?? [];
  // Референс ProductPurchaseDetails.js:123-136: при возврате вместо даты
  // окончания доступа показывается дата возврата.
  const refundedAt = findLastSucceededRefund(refunds)?.createdAt;
  // Просрочка подписки (спека viewer-money A.6, ревью M6): причина и ход
  // попыток видны и в карточке покупки, а не только в списке подписок.
  const pastDueProgress = subscription
    ? formatPastDue(subscription, t, (value) =>
        interpolationFormat(value, "dateTime", i18n.language)
      )
    : null;

  const formatDateTime = (value: string): string =>
    interpolationFormat(value, "dateTime", i18n.language);

  const formatBoolean = (value: boolean): string =>
    t(value ? "common.yes" : "common.no");

  return (
    <Dialog
      fullScreen={fullScreen}
      fullWidth
      maxWidth="md"
      scroll="body"
      open={open}
      onClose={onClose}
    >
      {/* Референс ProductPurchaseDialog.js:42-46: шапка залипает при
          прокрутке содержимого диалога. */}
      <Box
        sx={{ position: "sticky", top: 0, zIndex: 1 }}
        bgcolor="background.paper"
      >
        <DialogHeader title={title} onClose={onClose} />
      </Box>
      <DialogContent sx={{ p: 1 }}>
        <Paper sx={{ p: 2 }}>
          {purchase && (
            // Две колонки полей и блок сумм во всю ширину: референс
            // whitelabelAdmin/components/PaymentDetails/PaymentDetails.js
            // (Grid size={{ xs: 12, md: 6 }} + AmountsGrid во всю ширину).
            // Порядок полей взят из колонок списка покупок референса
            // (whitelabelAdmin/routes/ProductPurchases/ProductPurchases.js):
            // продукт, покупатель с чипами типа и возврата, автопродление,
            // суммы, период, дата покупки, действует до.
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Field label={t("purchases.details.productLabel")}>
                  {purchase.product ? (
                    <MuiLink
                      component={RouterLink}
                      target="_blank"
                      to={`${PagePaths.products}?tab=edit&id=${purchase.product.id}`}
                    >
                      {purchase.product.title}
                    </MuiLink>
                  ) : (
                    emptyValue
                  )}
                </Field>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Field label={t("purchases.details.buyerLabel")}>
                  {purchase.buyerEmail ? (
                    <MuiLink
                      component={RouterLink}
                      color="secondary"
                      target="_blank"
                      to={`${PagePaths.users}?tab=details&id=${purchase.userId}`}
                    >
                      {purchase.buyerEmail}
                    </MuiLink>
                  ) : (
                    emptyValue
                  )}
                </Field>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Field label={t("purchases.details.typeLabel")}>
                  {purchase.type}
                </Field>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Field label={t("purchases.details.providerLabel")}>
                  {purchase.payment?.provider
                    ? t(PAYMENT_PROVIDER_LABEL_KEY[purchase.payment.provider])
                    : emptyValue}
                </Field>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Field label={t("purchases.details.refundedLabel")}>
                  <Chip
                    size="small"
                    color={purchase.refunded ? "error" : "default"}
                    label={formatBoolean(purchase.refunded)}
                  />
                </Field>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Field label={t("purchases.details.disputedLabel")}>
                  <Chip
                    size="small"
                    color={purchase.disputed ? "error" : "default"}
                    label={formatBoolean(purchase.disputed)}
                  />
                </Field>
              </Grid>
              {purchase.linkReward && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field label={t("purchases.details.linkRewardLabel")}>
                    {formatMoney(
                      purchase.linkReward.value,
                      purchase.linkReward.currency,
                      i18n.language
                    )}
                  </Field>
                </Grid>
              )}
              {purchase.subscription && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <MuiLink
                      component={RouterLink}
                      to={`${PagePaths.subscriptions}?id=${purchase.subscription.id}`}
                    >
                      {t("purchases.details.viewSubscriptionLink")}
                    </MuiLink>
                  </Stack>
                </Grid>
              )}
              {payment && (
                <Grid size={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t("purchases.details.breakdownTitle")}
                  </Typography>
                  <PaymentBreakdown payment={payment} />
                </Grid>
              )}
              {refunds.length > 0 && (
                <Grid size={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t("purchases.details.refundsTitle")}
                  </Typography>
                  <PaymentRefundsTable refunds={refunds} />
                </Grid>
              )}
              <Grid size={{ xs: 12, sm: 6 }}>
                <Field label={t("purchases.details.periodLabel")}>
                  {purchase.payment
                    ? t("purchases.list.periodDays", {
                        count: purchase.payment.period,
                      })
                    : emptyValue}
                </Field>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Field label={t("purchases.details.createdAtLabel")}>
                  {payment ? formatDateTime(payment.createdAt) : emptyValue}
                </Field>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Field label={t("purchases.details.startAtLabel")}>
                  {formatDateTime(purchase.startAt)}
                </Field>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                {refundedAt ? (
                  <Field label={t("purchases.details.refundedAtLabel")}>
                    {formatDateTime(refundedAt)}
                  </Field>
                ) : (
                  <Field label={t("purchases.details.expiredAtLabel")}>
                    {formatDateTime(purchase.expiredAt)}
                  </Field>
                )}
              </Grid>
              {payment && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field label={t("purchases.details.recurrentLabel")}>
                    {formatBoolean(payment.recurrent)}
                  </Field>
                </Grid>
              )}
              {subscription && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field label={t("purchases.details.renewSubscriptionLabel")}>
                    {formatBoolean(subscription.renewSubscription)}
                  </Field>
                </Grid>
              )}
              {subscription && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field
                    label={t("purchases.details.billingCyclesCompleteLabel")}
                  >
                    {subscription.billingCyclesComplete}
                  </Field>
                </Grid>
              )}
              {subscription && pastDueProgress && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field label={t("subscriptions.details.renewErrorLabel")}>
                    {subscription.renewErrorReason
                      ? t(
                          RENEW_ERROR_REASON_LABEL_KEYS[
                            subscription.renewErrorReason
                          ]
                        )
                      : emptyValue}
                  </Field>
                </Grid>
              )}
              {pastDueProgress && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field label={t("subscriptions.details.renewAttemptLabel")}>
                    {pastDueProgress}
                  </Field>
                </Grid>
              )}
            </Grid>
          )}
        </Paper>
      </DialogContent>
    </Dialog>
  );
};
